import {
  AffiliateService, TrackingAction, TrackingCategory, trackingService,
} from '.';
import {
  type IPaddleCheckoutOpenConfig,
  type IPaddleCheckoutResponseData,
  type IPaddleOrderDetailsOrder,
  type IPaddleOrderResponseData,
  PaddleEvents,
  type PriceInfo,
} from '../types';
import { getIpInfoLocalization } from './ipInfo.service';

const PADDLE_VENDOR_ID = 129508;

const paddle = typeof window !== 'undefined' && window.Paddle;

const openCheckoutWithIpInfo = async (params: IPaddleCheckoutOpenConfig) => {
  let paddleParams = params;
  await getIpInfoLocalization()
    .then((res) => {
      let allowMarketing = false;
      // check marketing flag for
      // US, Canada, Australia, China, Taiwan, South Korea, India,
      // Brazil, Indonesia, Japan, Hong Kong, Türkiye
      const allowedMarketingCountryCodes = [
        'US',
        'CA',
        'AU',
        'CN',
        'TW',
        'KR',
        'IN',
        'BR',
        'ID',
        'JP',
        'HK',
        'TR',
      ];
      if (allowedMarketingCountryCodes.includes(res.country)) {
        allowMarketing = true;
      }
      paddleParams = {
        ...paddleParams,
        postcode: res.postCode,
        country: res.country,
        marketingConsent: allowMarketing ? '1' : '0',
      };
    })
    .finally(() => {
      paddle.Checkout.open(paddleParams);
    });
};

if (
  paddle
  && !window.location.href.includes('auth-test.klang.io')
  && !window.location.href.includes('auth.klang.io')
) {
  paddle.Setup({
    vendor: PADDLE_VENDOR_ID,
    eventCallback: (data) => {
      console.debug('PaddleCheckoutEvent: ', data);
      if (data.event === PaddleEvents.CHECKOUT_LOGIN) {
        // user past the email checkout step
        trackingService.event(TrackingCategory.Checkout, TrackingAction.CHECKOUT_PADDLE_PAST_EMAIL);
      } else if (data.event === PaddleEvents.CHECKOUT_LOCATION_SUBMIT) {
        // user past the location page
        trackingService.event(
          TrackingCategory.Checkout,
          TrackingAction.CHECKOUT_PADDLE_PAST_LOCATION,
        );
      } else if (data.event === PaddleEvents.CHECKOUT_PAYMENT_SELECTED) {
        // user past the payment selection
        trackingService.event(
          TrackingCategory.Checkout,
          TrackingAction.CHECKOUT_PADDLE_PAST_PAYMENT,
        );
      } else if (data.event === PaddleEvents.CHECKOUT_PAYMENT_COMPLETE) {
        // payment was successfull via paddle
        trackingService.event(
          TrackingCategory.Checkout,
          TrackingAction.CHECKOUT_PADDLE_PAST_PURCHASE,
        );
      } else if (data.event === PaddleEvents.CHECKOUT_USER_SUBSCRIBED) {
        trackingService.event(
          TrackingCategory.Marketing,
          TrackingAction.MARKETING_PADDLE_SUBSCRIBE,
        );
      } else if (data.event === PaddleEvents.CHECKOUT_COMPLETE) {
        if (window.dataLayer) {
          const dataLayerInfo = {
            event: 'purchase',
            ecommerce: {
              transaction_id: data.eventData.checkout.id,
              affiliation: 'Insert Your Affiliation',
              value: data.eventData.checkout.prices.customer.total,
              tax: data.eventData.checkout.prices.customer.total_tax,
              shipping: null as null,
              currency: data.eventData.checkout.prices.customer.currency,
              coupon: data.eventData.checkout.coupon.coupon_code,
              items: data.eventData.checkout.prices.customer.items,
            },
          };

          window.dataLayer.push(dataLayerInfo);
        }
      }
    },
  });
}

const POLL_INTERVALL_IN_MS: number = 1200;
const POLL_INTERVALL_OFFSET_IN_MS: number = 2500;

const PURCHASE_CLOSE_MSG = 'User closed the purchase process.';
const PURCHASE_MAX_ORDER_POLL_REACHED = 'Polling for order and order_id reached maximum.';

export interface GiftCardOption {
  /**
   * Path of the selected coverImage.
   */
  coverImage: string;
  /**
   * Amount of transcriptions contained in the gift card.
   */
  amount: number;
  senderName: string;
  recipientName: string;
  message: string;
}

/**
 * CheckoutService providing an async, promise based interface.
 */
export class AsyncCheckoutService {
  /**
   * Fetch price info from paddle given the product id
   * @param productId
   * @returns PriceInfo containing info about price, subscription, tax
   */
  static getPriceInfo(productId: number | string): Promise<PriceInfo> {
    return new Promise((resolve, reject) => {
      try {
        paddle.Product.Prices(productId, 1, (data: PriceInfo) => {
          // replace US$ by just $
          if (data.price.gross.startsWith('US$')) {
            resolve({
              ...data,
              price: {
                ...data.price,
                gross: data.price.gross.replace('US$', '$'),
              },
              productId,
            });
          } else {
            resolve({ ...data, productId });
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Fetch the gross price of the product from paddle, given the product Id.
   * @param productId Id of the product the price should be fetched for.
   * @returns Promise resolves to the gross price of the product.
   */
  static async getPrice(productId: number): Promise<string> {
    return (await this.getPriceInfo(productId)).price.gross;
  }

  /**
   * Start the purchase process for a GiftCard. Returns a
   * promise that resolves with the correct order id
   * or rejects if the user closed the purchase process.
   * @param {GiftCardOption} options Options to passthrough the payment process.
   * @param {number} productId Id of the product to purchase.
   * @param {string | undefined} title Tilte of the gift card product.
   * @returns {Promise} Promise that resolves with the order id and rejects with a reason.
   */
  static buyGiftCard(
    options: GiftCardOption,
    productId: number | string,
    title?: string,
  ): Promise<IPaddleOrderDetailsOrder> {
    // track open checkout event
    trackingService.event(TrackingCategory.Checkout, TrackingAction.CHECKOUT_PADDLE_ECARD_OPEN);

    return new Promise((resolve, reject) => {
      const checkoutComplete = (data: IPaddleCheckoutResponseData) => {
        // @ts-ignore allow unused tag here
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const TAG = '[BuyEcard CheckoutComplete]';
        // track success event
        trackingService.event(
          TrackingCategory.Checkout,
          TrackingAction.CHECKOUT_PADDLE_ECARD_SUCCESS,
        );

        const pollFunction = AsyncCheckoutService.getFunctionToPollForOrderID(
          productId,
          title,
          data,
          resolve,
          reject,
        );
        setTimeout(pollFunction, POLL_INTERVALL_IN_MS + POLL_INTERVALL_OFFSET_IN_MS);
      };

      const checkoutClose = () => {
        // track abort event
        trackingService.event(
          TrackingCategory.Checkout,
          TrackingAction.CHECKOUT_PADDLE_ECARD_ABORT,
        );
        reject(PURCHASE_CLOSE_MSG);
      };

      openCheckoutWithIpInfo({
        product: productId,
        quantity: 0,
        allowQuantity: false,
        title,
        passthrough: JSON.stringify(options),
        successCallback: checkoutComplete,
        closeCallback: checkoutClose,
        // simplify reporting in paddle dashboard
        referring_domain: '',
      });
    });
  }

  /**
   * Returns a function to be used in Intervall oder Timeouts within
   * Promises to fetch the order id after the checkout is completed.
   * @param productId Id of the product purchased in the order.
   * @param title Title of the product purchased in the order.
   * @param data Checkout data object.
   * @param resolve Function to resolve the promise using the poll function.
   * @param reject Function to reject the promise using the poll function.
   * @returns Function to internally poll for the OrderId.
   */
  static getFunctionToPollForOrderID(
    productId: number | string,
    // Don't break order for users of function
    // eslint-disable-next-line default-param-last
    title: string = undefined,
    data: IPaddleCheckoutResponseData,
    resolve: (value: IPaddleOrderDetailsOrder | PromiseLike<IPaddleOrderDetailsOrder>) => void,
    reject: (reason?: any) => void,
  ) {
    const TAG = '[CheckOrderIdPolling]';
    let pollCounter = 0;
    return function checkOrderIDAndUseCallback() {
      paddle.Order.details(data.checkout.id, (responseData: IPaddleOrderResponseData) => {
        paddle.Spinner.show();
        // check if paddle responsa data has an order attached to it.
        if (
          responseData
          && responseData.state === 'processed'
          && 'order' in responseData
          && 'order_id' in responseData.order
        ) {
          const { order } = responseData;
          trackingService.purchase(
            `${order.order_id}`,
            `${productId}`,
            title,
            parseFloat(order.total),
            order.currency,
          );
          if (order.coupon_code) {
            try {
              AffiliateService.handleAffiliateTracking(
                order.order_id,
                order.total,
                order.coupon_code,
                order.currency,
              );
            } catch (err) {
              console.log('Error occurred when calling AffiliateService.');
              console.log(err);
            }
          }
          console.log(TAG, 'Had to externally poll for', pollCounter, 'times.');
          paddle.Spinner.hide();
          resolve(order);
        } else {
          pollCounter += 1;
          if (pollCounter >= 10) {
            paddle.Spinner.hide();
            trackingService.event(
              TrackingCategory.Checkout,
              TrackingAction.CHECKOUT_PADDLE_MISSING_ORDER_ID_FAIL,
            );
            reject(PURCHASE_MAX_ORDER_POLL_REACHED);
            return;
          }
          trackingService.event(
            TrackingCategory.Checkout,
            TrackingAction.CHECKOUT_PADDLE_MISSING_ORDER_ID,
          );
          setTimeout(checkOrderIDAndUseCallback, POLL_INTERVALL_IN_MS);
        }
      });
    };
  }
}
